import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FiRefreshCw, FiDownload, FiFileText, FiClock, FiCheckCircle, FiUsers, FiDollarSign,
  FiTrendingUp, FiCreditCard, FiArrowRight, FiUser,
} from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import { useCurrency } from '../../app/CurrencyContext';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import Drawer from '../../components/Drawer/Drawer';
import drawerStyles from '../../components/Drawer/Drawer.module.css';
import Pagination from '../../components/Pagination/Pagination';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from './AdminPayments.module.css';
import axiosClient from '../../api/axiosClient';

interface Stats {
  totalTransactions: number; totalVolume: number; pendingReview: number; pendingVolume: number;
  approved: number; approvedVolume: number; uniqueUsers: number; approvalRate: number;
  bankCount: number; upiCount: number;
  recentTransactions: { id: string; amount: string; currency: string; status: string; payment_method_type: string; created_at: string; full_name: string; email: string }[];
}

interface Payment {
  id: string; amount: string; currency: string; status: string;
  transaction_ref: string | null; payment_method_type: string | null; proof_path: string | null;
  payment_date: string | null; package_name: string | null; created_at: string; full_name: string; email: string;
}

const CURRENCY_SYMBOL: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', USDT: '' };
const STATUS_COLORS: Record<string, string> = {
  pending: '#e8a86a', processing: '#1d60f6', paid: '#5cb87d', rejected: '#e2534a',
};
const TABS = ['overview', 'requests', 'analytics'] as const;

const AdminPayments: React.FC = () => {
  const { formatFromInr, currencies: adminCurrencies } = useCurrency();
  const [stats, setStats] = useState<Stats | null>(null);
  const [tab, setTab] = useState<(typeof TABS)[number]>('overview');
  const [requests, setRequests] = useState<Payment[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState<Payment | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 8;

  const loadStats = () => {
    axiosClient.get('/admin/payments/stats').then(({ data }) => setStats(data));
  };
  const loadRequests = () => {
    setLoadingRequests(true);
    axiosClient.get('/admin/payments', { params: filter !== 'all' ? { status: filter } : {} })
      .then(({ data }) => setRequests(data.requests))
      .finally(() => setLoadingRequests(false));
  };

  useEffect(() => { loadStats(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRequests(); }, [filter]);

  const updateStatus = async (id: string, status: string) => {
    try {
      await axiosClient.patch(`/admin/payments/${id}/status`, { status });
      toast.success(`Marked as ${status}.`);
      setSelected(null);
      loadRequests();
      loadStats();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update status.');
    }
  };

  const exportCsv = () => {
    const rows = requests.map((r) => [r.full_name, r.email, r.amount, r.currency, r.status, r.transaction_ref || '', r.created_at]);
    const csv = ['Name,Email,Amount,Currency,Status,Transaction Ref,Date', ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'payment-requests.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminShell>
      <PageHeader
        title="Payment Management"
        subtitle="Manage and monitor all payment transactions"
        action={
          <div className={styles.headerActions}>
            <AdminWalletBadge />
            <button onClick={() => { loadStats(); loadRequests(); }}><FiRefreshCw /> Refresh</button>
            <button className={styles.exportBtn} onClick={exportCsv}><FiDownload /> Export CSV</button>
          </div>
        }
      />

      <div className={panelStyles.page}>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div><span>Total Transactions</span><b>{stats?.totalTransactions ?? 0}</b><small>All time</small></div>
            <FiFileText className={styles.statIcon} />
          </div>
          <div className={styles.statCard}>
            <div><span>Pending Review</span><b className={styles.warnText}>{stats?.pendingReview ?? 0}</b><small>{formatFromInr(stats?.pendingVolume ?? 0)}</small></div>
            <FiClock className={`${styles.statIcon} ${styles.iconWarn}`} />
          </div>
          <div className={styles.statCard}>
            <div><span>Approved</span><b className={styles.successText}>{stats?.approved ?? 0}</b><small>{formatFromInr(stats?.approvedVolume ?? 0)}</small></div>
            <FiCheckCircle className={`${styles.statIcon} ${styles.iconSuccess}`} />
          </div>
          <div className={styles.statCard}>
            <div><span>Unique Users</span><b>{stats?.uniqueUsers ?? 0}</b><small>Active participants</small></div>
            <FiUsers className={styles.statIcon} />
          </div>
        </div>

        <div className={styles.statsGrid2}>
          <div className={styles.volumeCard}>
            <div><span>Total Volume</span><b>{formatFromInr(stats?.totalVolume ?? 0)}</b><small>Processing fee: 0%</small></div>
            <FiDollarSign className={styles.volumeIcon} />
          </div>
          <div className={styles.statCard}>
            <div><span>Approval Rate</span><b>{stats?.approvalRate ?? 0}%</b></div>
            <FiTrendingUp className={styles.statIcon} />
          </div>
          <div className={styles.statCard}>
            <div>
              <span>Payment Methods</span>
              <div className={styles.methodRow}><span>Bank</span><b>{stats?.bankCount ?? 0}</b><span>UPI</span><b>{stats?.upiCount ?? 0}</b></div>
            </div>
            <FiCreditCard className={styles.statIcon} />
          </div>
        </div>

        <div className={styles.tabs}>
          <button className={tab === 'overview' ? styles.tabActive : ''} onClick={() => setTab('overview')}>Overview</button>
          <button className={tab === 'requests' ? styles.tabActive : ''} onClick={() => setTab('requests')}>Payment Requests <span className={styles.tabCount}>{requests.length}</span></button>
          <button className={tab === 'analytics' ? styles.tabActive : ''} onClick={() => setTab('analytics')}>Analytics</button>
        </div>

        {tab === 'overview' && (
          <div className={styles.overviewGrid}>
            <div className={styles.card}>
              <h3>Recent Transactions</h3>
              {(!stats || stats.recentTransactions.length === 0) && <div className={panelStyles.muted}>No transactions yet.</div>}
              {stats?.recentTransactions.map((t) => (
                <div key={t.id} className={styles.txRow}>
                  <div><b>{t.full_name}</b><div className={panelStyles.subCell}>{t.email}</div></div>
                  <div className={styles.txRight}>
                    <b>{adminCurrencies.find((c) => c.code === t.currency)?.symbol || CURRENCY_SYMBOL[t.currency] || t.currency + ' '}{Number(t.amount).toLocaleString()}</b>
                    <span className={panelStyles.statusPill} style={{ color: STATUS_COLORS[t.status], borderColor: STATUS_COLORS[t.status] }}>{t.status}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.card}>
              <h3>Quick Actions</h3>
              <button className={styles.quickAction} onClick={() => setTab('requests')}>Review Pending Payments <FiArrowRight /></button>
              <button className={styles.quickAction} onClick={() => { loadStats(); loadRequests(); toast.success('Data synced.'); }}>Sync Latest Data <FiRefreshCw /></button>
              <button className={styles.quickAction} onClick={exportCsv}>Export Reports <FiDownload /></button>
            </div>
          </div>
        )}

        {tab === 'requests' && (
          <>
            <div className={panelStyles.filters}>
              {['all', 'pending', 'processing', 'paid', 'rejected'].map((f) => (
                <button key={f} className={`${panelStyles.filterBtn} ${filter === f ? panelStyles.filterBtnActive : ''}`} onClick={() => setFilter(f)}>
                  {f[0].toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <div className={panelStyles.tableWrap}>
              {loadingRequests && <div className={panelStyles.loading}><span className={panelStyles.spinnerLg} /></div>}
              {!loadingRequests && requests.length === 0 && <div className={panelStyles.empty}>No payment requests match this filter.</div>}
              {!loadingRequests && requests.length > 0 && (
                <table className={panelStyles.table}>
                  <thead><tr><th>User</th><th>Amount</th><th>Purpose</th><th>Method</th><th>Status</th></tr></thead>
                  <tbody>
                    {requests.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((r) => (
                      <tr key={r.id} onClick={() => setSelected(r)} style={{ cursor: 'pointer' }}>
                        <td><div className={panelStyles.nameCell}>{r.full_name}</div><div className={panelStyles.subCell}>{r.email}</div></td>
                        <td className={panelStyles.nameCell}>{adminCurrencies.find((c) => c.code === r.currency)?.symbol || CURRENCY_SYMBOL[r.currency] || r.currency + ' '}{Number(r.amount).toLocaleString()} <span style={{ color: 'var(--muted)', fontWeight: 500, fontSize: 11 }}>({r.currency})</span></td>
                        <td>
                          {r.package_name ? (
                            <span className={panelStyles.statusPill} style={{ color: '#a855f7', borderColor: '#a855f7' }}>{r.package_name}</span>
                          ) : <span className={panelStyles.subCell}>General deposit</span>}
                        </td>
                        <td>{r.payment_method_type || '—'}</td>
                        <td><span className={panelStyles.statusPill} style={{ color: STATUS_COLORS[r.status], borderColor: STATUS_COLORS[r.status] }}>{r.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!loadingRequests && requests.length > 0 && (
                <Pagination page={page} totalPages={Math.max(1, Math.ceil(requests.length / PAGE_SIZE))} onChange={setPage} />
              )}
            </div>
          </>
        )}

        {tab === 'analytics' && (
          <div className={styles.card}>
            <h3>Analytics</h3>
            <ul className={styles.analyticsList}>
              <li><span>Approval rate</span><b>{stats?.approvalRate ?? 0}%</b></li>
              <li><span>Bank transfer share</span><b>{stats && (stats.bankCount + stats.upiCount) > 0 ? Math.round((stats.bankCount / (stats.bankCount + stats.upiCount)) * 100) : 0}%</b></li>
              <li><span>UPI share</span><b>{stats && (stats.bankCount + stats.upiCount) > 0 ? Math.round((stats.upiCount / (stats.bankCount + stats.upiCount)) * 100) : 0}%</b></li>
              <li><span>Avg. transaction volume</span><b>{stats && stats.totalTransactions > 0 ? formatFromInr(Math.round(stats.totalVolume / stats.totalTransactions)) : formatFromInr(0)}</b></li>
            </ul>
          </div>
        )}
      </div>

      <Drawer open={!!selected} onClose={() => setSelected(null)} title="Payment request">
        {selected && (
          <>
            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}><FiUser style={{ marginRight: 6 }} />Account</div>
              <div className={drawerStyles.detailValue}>{selected.full_name}</div>
              <div className={panelStyles.subCell}>{selected.email}</div>
            </div>
            <div className={drawerStyles.detailGrid}>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Amount</div>
                <div className={drawerStyles.detailValue}>{adminCurrencies.find((c) => c.code === selected.currency)?.symbol || CURRENCY_SYMBOL[selected.currency] || selected.currency + ' '}{Number(selected.amount).toLocaleString()}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Currency</div>
                <div className={drawerStyles.detailValue}>{selected.currency}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Purpose</div>
                <div className={drawerStyles.detailValue}>{selected.package_name || 'General deposit'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Method</div>
                <div className={drawerStyles.detailValue}>{selected.payment_method_type || '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Transaction Ref</div>
                <div className={drawerStyles.detailValue}>{selected.transaction_ref || '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Payment Date &amp; Time</div>
                <div className={drawerStyles.detailValue}>{selected.payment_date ? new Date(selected.payment_date).toLocaleString() : '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Submitted</div>
                <div className={drawerStyles.detailValue}>{new Date(selected.created_at).toLocaleString()}</div>
              </div>
            </div>
            {selected.proof_path && (
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Payment proof</div>
                <a className={panelStyles.docLink} href={`${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}${selected.proof_path}`} target="_blank" rel="noreferrer">View screenshot</a>
              </div>
            )}
          </>
        )}
        {selected && (
          <div className={drawerStyles.footer} style={{ position: 'sticky', bottom: 0, background: 'var(--panel)', margin: '24px -24px -24px', flexWrap: 'wrap' }}>
            {selected.status !== 'processing' && selected.status !== 'paid' && selected.status !== 'rejected' && (
              <button className={panelStyles.actionBtn} onClick={() => updateStatus(selected.id, 'processing')}>Mark Processing</button>
            )}
            {selected.status !== 'paid' && selected.status !== 'rejected' && (
              <button className={panelStyles.actionBtn} onClick={() => updateStatus(selected.id, 'paid')}>Approve</button>
            )}
            {selected.status !== 'rejected' && selected.status !== 'paid' && (
              <button className={panelStyles.actionBtnReject} onClick={() => updateStatus(selected.id, 'rejected')}>Reject</button>
            )}
          </div>
        )}
      </Drawer>
    </AdminShell>
  );
};

export default AdminPayments;
